import { useState } from "react";
import { Loader2, Sparkles, Wand2, Mail, Lock, MessageSquare, CheckCircle2, ArrowRight, Bot, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { API_URL } from "../utils/api";

/**
 * LockedDraftTeaser
 *
 * Conversion-first reveal: the anonymous draft analysis is shown
 * (company name, agent persona, one demo Q&A in the clear) and the
 * rest of the value (FAQ library, services, "activate live" button)
 * is blurred behind a glass overlay with the auth CTA on top.
 *
 * Auth → POST /api/onboarding/draft-anon/<draft_id>/claim with
 * X-Anon-Token header → navigate to /skapa-ai-agent/utkast/<draft_id>.
 *
 * Props
 *   - draft        : serialized draft from /api/onboarding/draft-anon/<id>
 *   - anonToken    : the client-generated token used during analyze-anon
 *   - googleEnabled: whether the Google OAuth path is configured
 *   - onAuthSuccess: callback(draftId) after a successful login + claim
 *   - onReset      : callback to abort the teaser flow and start over
 *   - requestCode  : OnboardingAuthContext.requestCode
 *   - verifyCode   : OnboardingAuthContext.verifyCode
 *   - applyToken   : OnboardingAuthContext.applyToken (used for Google return)
 */
const PENDING_URL_KEY = "elido_onboarding_pending_url";
const PENDING_CLAIM_KEY = "elido_onboarding_pending_claim";

export function LockedDraftTeaser({
  draft,
  anonToken,
  googleEnabled,
  onAuthSuccess,
  onReset,
  requestCode,
  verifyCode,
}) {
  const [mode, setMode] = useState(googleEnabled ? "choose" : "email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const companyName = draft?.company_name || _hostnameFromUrl(draft?.website_url) || "Ditt företag";
  const industry = draft?.detected_industry || "Service & rådgivning";
  const services = (draft?.services || []).slice(0, 4);
  const faqs = (draft?.suggested_faqs || []).filter(Boolean);
  const firstFaq = faqs[0];
  const blurredFaqs = faqs.slice(1, 4);
  const tone = draft?.tone_voice || "Vänligt, säljande och tydligt — du-form";
  const agentLabel = `Maja — AI-agent för ${companyName}`;

  const triggerGoogle = () => {
    try {
      if (anonToken && draft?.draft_id) {
        localStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify({
          draft_id: draft.draft_id,
          anon_token: anonToken,
        }));
      }
      // Clear pending-url so we don't re-analyze on return
      localStorage.removeItem(PENDING_URL_KEY);
    } catch (_) { /* ignore */ }
    const returnUrl = "/skapa-ai-agent";
    window.location.href = `${API_URL}/api/onboarding/auth/google/start?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { toast.error("Ange e-post"); return; }
    setBusy(true);
    try {
      await requestCode(email.trim());
      toast.success("Vi skickade en kod till din e-post");
      setMode("code");
    } catch (err) {
      toast.error(err.message || "Kunde inte skicka kod");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) { toast.error("Ange koden"); return; }
    setBusy(true);
    try {
      await verifyCode(email.trim(), code.trim());
      // verifyCode just logged in; claim the anon draft now.
      const token = _readToken();
      const res = await fetch(`${API_URL}/api/onboarding/draft-anon/${draft.draft_id}/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Anon-Token": anonToken || "",
          Authorization: `Bearer ${token || ""}`,
        },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Kunde inte länka utkastet");
      }
      try { localStorage.removeItem(PENDING_CLAIM_KEY); } catch (_) { /* ignore */ }
      onAuthSuccess?.(draft.draft_id);
    } catch (err) {
      toast.error(err.message || "Fel kod");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white relative overflow-hidden"
      data-testid="onboarding-locked-teaser"
    >
      {/* Subtle grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="flex items-center gap-2" data-testid="teaser-logo">
            <img src="/elido-logo.png" alt="Elido" className="h-7 w-auto" />
          </a>
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-white/50 hover:text-white/85 transition-colors"
            data-testid="teaser-reset-btn"
          >
            ← Starta om med annan URL
          </button>
        </div>

        {/* Hero strip — agent identity (CLEAR / not blurred) */}
        <header className="mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] bg-[#3A8A98]/15 border border-[#3A8A98]/30 rounded-full px-3 py-1.5">
            <Sparkles className="w-3 h-3" />
            Ditt utkast är klart
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight">
            Vi byggde en AI-agent för{" "}
            <span className="bg-gradient-to-r from-[#7FD4DE] via-[#A8E6D8] to-[#7FD4DE] bg-clip-text text-transparent">
              {companyName}
            </span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/60">
            Bransch: <span className="text-white/85">{industry}</span> · Tonläge: <span className="text-white/85">{tone}</span>
          </p>
        </header>

        {/* CLEAR teaser block — agent card + first demo Q&A */}
        <section
          className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5 sm:p-7 shadow-2xl shadow-black/30"
          data-testid="teaser-clear-block"
        >
          <div className="flex items-start gap-4 mb-5 pb-5 border-b border-white/10">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] flex items-center justify-center shrink-0 shadow-lg shadow-[#3A8A98]/30">
              <Bot className="w-6 h-6 text-[#0F2A30]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white text-base">{agentLabel}</div>
              <div className="text-[12px] text-white/55 mt-0.5 truncate">
                Tränad på {(draft?.scraped_context?.final_url || draft?.website_url) || "din hemsida"}
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 rounded-full px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Aktiv förhandsvisning
            </span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40 mb-3 flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> Exempelsamtal
          </div>

          {firstFaq ? (
            <div className="space-y-3" data-testid="teaser-demo-conversation">
              {/* User question */}
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-[#7FD4DE]/15 border border-[#7FD4DE]/25 text-white/95 text-sm rounded-2xl rounded-tr-md px-4 py-2.5">
                  {firstFaq.question || "Vad kostar det att börja jobba med er?"}
                </div>
              </div>
              {/* Agent answer */}
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-[#0F2A30]" />
                </div>
                <div className="max-w-[85%] bg-white/[0.05] border border-white/10 text-white/85 text-sm rounded-2xl rounded-tl-md px-4 py-2.5 leading-relaxed">
                  {firstFaq.answer || "Vi har transparenta paket från 990 kr/mån. Vill du att jag mejlar en sammanställning?"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-white/50 italic">
              Inga vanliga frågor hittades på sidan — ditt fullständiga utkast innehåller en standardmall.
            </div>
          )}
        </section>

        {/* BLURRED block — the rest of the draft + auth overlay */}
        <section className="relative mt-6">
          {/* The blurred preview content */}
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-7 space-y-4 filter blur-[6px] select-none pointer-events-none"
            aria-hidden="true"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">FAQ-bibliotek</div>
            {(blurredFaqs.length ? blurredFaqs : DEFAULT_GHOST_FAQS).map((f, i) => (
              <div key={i} className="border-l-2 border-[#7FD4DE]/40 pl-4">
                <div className="text-sm font-semibold text-white">{f.question}</div>
                <div className="text-sm text-white/60 mt-1">{f.answer}</div>
              </div>
            ))}
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40 mt-6">
              Tjänster vi hittade
            </div>
            <div className="flex flex-wrap gap-2">
              {(services.length ? services : DEFAULT_GHOST_SERVICES).map((s, i) => (
                <span key={i} className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1 text-white/80">
                  {typeof s === "string" ? s : (s.name || s.title || "Tjänst")}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Integration</div>
                <div className="text-xs text-white/60 font-mono">{`<script src="https://elido.nu/widget.js" data-client="…"></script>`}</div>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Aktivera live</div>
                <div className="text-xs text-white/60">14 dagar gratis · ingen bindning</div>
              </div>
            </div>
          </div>

          {/* Auth overlay (CTA on top of blur) */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              className="w-full max-w-md bg-gradient-to-b from-[#0F2A30]/95 to-[#0a1f24]/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
              data-testid="teaser-auth-card"
            >
              <div className="px-6 pt-7 pb-2 text-center">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-3">
                  <Lock className="w-3 h-3" />
                  Lås upp ditt utkast
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-tight">
                  Bara ett mejl bort
                </h2>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                  Vi sparar utkastet på ditt konto. Inga lösenord, ingen bindning.
                </p>
              </div>

              <div className="p-6 pt-4">
                {mode === "choose" && googleEnabled && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={triggerGoogle}
                      className="w-full flex items-center justify-center gap-2.5 bg-white text-slate-900 font-semibold rounded-xl py-3 hover:bg-slate-50 transition-colors shadow-md"
                      data-testid="teaser-google-btn"
                    >
                      <GoogleIcon />
                      <span>Fortsätt med Google</span>
                    </button>
                    <div className="flex items-center gap-3 my-1">
                      <div className="flex-1 h-px bg-white/10" />
                      <span className="text-[10px] uppercase tracking-wider text-white/40">eller</span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setMode("email")}
                      className="w-full flex items-center justify-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-white/85 rounded-xl py-3 transition-colors"
                      data-testid="teaser-email-btn"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="text-sm font-medium">Använd e-post & kod</span>
                    </button>
                  </div>
                )}

                {mode === "email" && (
                  <form onSubmit={sendCode} className="space-y-3" data-testid="teaser-email-form">
                    <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/50">E-post</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                      <Input
                        autoFocus
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ditt.namn@exempel.se"
                        className="pl-10 h-12 bg-[#0a1f24] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                        data-testid="teaser-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={busy}
                      className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                      data-testid="teaser-send-code-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                      Mejla mig en kod
                    </Button>
                    {googleEnabled && (
                      <button
                        type="button"
                        onClick={() => setMode("choose")}
                        className="block text-xs text-white/50 hover:text-white/80 mx-auto"
                      >
                        ← Tillbaka
                      </button>
                    )}
                  </form>
                )}

                {mode === "code" && (
                  <form onSubmit={submitCode} className="space-y-3" data-testid="teaser-code-form">
                    <p className="text-[12px] text-white/60">
                      Vi skickade en 6-siffrig kod till <strong className="text-white">{email}</strong>.
                    </p>
                    <Input
                      autoFocus
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      className="text-center text-xl tracking-[0.4em] h-12 bg-[#0a1f24] border-white/10 text-white placeholder:text-white/20 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                      data-testid="teaser-code-input"
                    />
                    <Button
                      type="submit"
                      disabled={busy || code.length !== 6}
                      className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                      data-testid="teaser-verify-code-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                      Lås upp utkastet
                    </Button>
                    <button
                      type="button"
                      onClick={() => setMode("email")}
                      className="block text-xs text-white/50 hover:text-white/80 mx-auto"
                    >
                      ← Byt e-post
                    </button>
                  </form>
                )}

                <ul className="mt-5 space-y-1.5 text-[11px] text-white/45">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Inget aktiveras live automatiskt</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> 14 dagars gratis test</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-emerald-400" /> GDPR-säker (data lagras i EU)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

const DEFAULT_GHOST_FAQS = [
  { question: "Vilka öppettider har ni?", answer: "Vår support är tillgänglig mån-fre 08-17. AI-agenten svarar dygnet runt." },
  { question: "Hur snabbt får jag offert?", answer: "Inom 24 timmar för standarduppdrag. AI-agenten samlar in alla detaljer direkt." },
  { question: "Vilka områden täcker ni?", answer: "Vi arbetar i hela Sverige med fokus på Stockholm, Göteborg och Malmö." },
];

const DEFAULT_GHOST_SERVICES = ["Konsultation", "Installation", "Service & support", "Skräddarsydda lösningar"];

function _hostnameFromUrl(u) {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch (_) { return ""; }
}

function _readToken() {
  try {
    return localStorage.getItem("elido_onboarding_jwt") || "";
  } catch (_) { return ""; }
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}
